// =============================================================================
// PreparedTopUpsPanel — staff see + cancel top-ups they have prepared for a service
// =============================================================================
// Shown on the DFV Service record (Details tab) to anyone who can Raise a Stock Top-up
// (DFV Services or Telemarketing access — all admin-level accounts, which the
// stock_top_up_prefill RLS lets read + update every row). Lists this service's PENDING
// staff-prepared top-ups: when, who prepared it, a one-line summary of the contents, and a
// Cancel button. Cancel is soft (status = 'cancelled' — the record is kept; the service's
// 📦 banner and link stop showing it) and audited. Hidden entirely when there are none.
// `reloadKey` — bump it to reload (e.g. after a new top-up is prepared).
// =============================================================================
import React from "react";
import { listProfilesByIds } from "../../data/profiles";
import { cancelPrefill, listPendingPrefillsForShelter } from "../../data/stockTopUpPrefill";
import { confirmDialog } from "../../services/dialogService";
import { topUpSummary } from "../../services/stockTopUpForm";
import { logAudit } from "../../utils/auditLog";

function fmtDate(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

export function PreparedTopUpsPanel({ shelterId, shelterCode, reloadKey }) {
  const [rows, setRows] = React.useState([]);
  const [names, setNames] = React.useState({});
  const [busyId, setBusyId] = React.useState(null);
  const [err, setErr] = React.useState("");
  const [tick, setTick] = React.useState(0);

  React.useEffect(
    function () {
      if (!shelterId) return undefined;
      let alive = true;
      listPendingPrefillsForShelter(shelterId).then(async function (list) {
        if (!alive) return;
        setRows(list || []);
        const ids = [...new Set((list || []).map((r) => r.prepared_by).filter(Boolean))];
        if (ids.length === 0) return;
        const people = await listProfilesByIds(ids);
        if (!alive) return;
        const map = {};
        for (const p of people || []) map[p.id] = p.display_name || p.staff_user_email || "";
        setNames(map);
      });
      return function () {
        alive = false;
      };
    },
    [shelterId, reloadKey, tick]
  );

  async function onCancel(row) {
    const ok = await confirmDialog({
      title: "Cancel this prepared top-up?",
      message:
        "The service will no longer see it or be able to submit it. The record is kept. This can't be undone — to send it again, prepare a new top-up.",
      confirmLabel: "Yes, cancel it",
      cancelLabel: "Keep",
      danger: true,
    });
    if (!ok) return;
    setErr("");
    setBusyId(row.id);
    const { error } = await cancelPrefill(row.id);
    setBusyId(null);
    if (error) {
      setErr(error.message || "Could not cancel — please try again.");
      setTick((t) => t + 1);
      return;
    }
    logAudit("top_up_prefill_cancelled", {
      prefill_id: row.id,
      shelter_id: shelterId,
      shelter: shelterCode || null,
    });
    setTick((t) => t + 1);
  }

  if (rows.length === 0 && !err) return null;

  return (
    <div className="border border-zephyr-border rounded-[10px] overflow-hidden">
      <div className="text-white px-[18px] py-2 font-bold text-sm bg-zephyr-teal-dark">
        📦 Prepared top-ups waiting for this service ({rows.length})
      </div>
      {err && <div className="px-[18px] pt-3 text-[13px] text-zephyr-red font-semibold">{err}</div>}
      {rows.map(function (r, i) {
        const summary = topUpSummary(r.payload).join(" · ") || "No items";
        const who = names[r.prepared_by] || "";
        return (
          <div
            key={r.id}
            className="flex items-center justify-between gap-3 py-[10px] px-[18px] text-[13px]"
            style={{ borderTop: i > 0 ? "1px solid #EEF3F2" : "none" }}
          >
            <div className="min-w-0">
              <div className="font-semibold text-zephyr-text">
                Prepared {fmtDate(r.created_at)}
                {who ? ` by ${who}` : ""}
              </div>
              <div className="text-[12px] text-zephyr-muted">{summary}</div>
            </div>
            <button
              type="button"
              onClick={function () {
                onCancel(r);
              }}
              disabled={busyId === r.id}
              aria-label={`Cancel the top-up prepared ${fmtDate(r.created_at)}`}
              className="bg-transparent rounded-[6px] py-1 px-[12px] text-[12px] cursor-pointer font-semibold shrink-0 disabled:opacity-50"
              style={{ border: "1px solid #C62828", color: "#C62828", fontFamily: "inherit" }}
            >
              {busyId === r.id ? "Cancelling…" : "Cancel"}
            </button>
          </div>
        );
      })}
    </div>
  );
}
